import { config } from "@/config";
import { useAuthHeaders } from "@/hooks/use-auth-headers";
import { useMutation } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api-client";

interface GenerateEmbeddingsRequest {
  projectUuid: string;
}

interface GenerateEmbeddingsResponse {
  success: boolean;
  message: string;
  data: {
    projectUuid: string;
  };
}

/**
 * Generate embeddings for all nodes in a project
 */
async function generateEmbeddings(
  data: GenerateEmbeddingsRequest,
  token: string
): Promise<GenerateEmbeddingsResponse> {
  const response = await apiFetch(
    `${config.isometricApiUrl}/functional-graph/v2/${data.projectUuid}/regenerate-embeddings?regenerate=true`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || "Failed to generate embeddings.");
  }

  return response.json();
}

/**
 * Hook to generate embeddings for a project
 */
export function useGenerateEmbeddings(
  onSuccess?: (data: GenerateEmbeddingsResponse) => void,
  onError?: (error: Error) => void
) {
  const { getAuthHeaders } = useAuthHeaders();

  return useMutation({
    mutationFn: (data: GenerateEmbeddingsRequest) => {
      const headers = getAuthHeaders();
      const token = headers.Authorization.replace("Bearer ", "");
      return generateEmbeddings(data, token);
    },
    onSuccess,
    onError,
  });
}
